import Model, { Request } from '../model'
import APIObject from '../object'
import Enum from '../enums'

export default class Billing extends Model {
  static modelName () {
    return 'billing'
  }

  static status () {
    const url = this.modelBaseURL() + '/status'
    return this.requestItem(Request.get(url), BillingStatus)
  }

  static plans () {
    const url = this.modelBaseURL() + '/plans'
    return this.requestList(Request.get(url), BillingPlan)
  }

  static methods () {
    const url = this.modelBaseURL() + '/methods'
    return this.requestList(Request.get(url), BillingMethod)
  }

  static token () {
    const url = this.modelBaseURL() + '/clientToken'
    return this.requestData(Request.get(url))
  }

  static cancel () {
    const url = this.modelBaseURL() + '/cancel'
    return this.requestSuccess(Request.get(url))
  }

  static payments (page = 1) {
    const url = this.modelBaseURL() + '/payments?page=' + page
    return this.requestList(Request.get(url), BillingPayments)
  }

  static create ({ nonce }) {
    const url = this.modelBaseURL() + '/methods'

    const data = {
      nonce
    }

    return this.requestItem(Request.post(url, JSON.stringify(data)), BillingMethod)
  }

  static default ({ nonce, deviceData }) {
    const url = this.modelBaseURL() + '/setDefaultMethod'

    const data = {
      nonce,
      deviceData
    }

    return this.requestItem(Request.post(url, JSON.stringify(data)), BillingMethod)
  }

  static delete ({ token }) {
    const url = this.modelBaseURL() + '/methods'

    const data = {
      token
    }

    return this.requestSuccess(Request.delete(url, JSON.stringify(data)))
  }

  static subscribe ({ plan, token, nonce, deviceData }) {
    const url = this.modelBaseURL() + '/subscribe'

    const data = {
      plan,
      token,
      nonce,
      deviceData
    }

    return this.requestSuccess(Request.post(url, JSON.stringify(data)))
  }

  static changePlan ({ plan }) {
    const url = this.modelBaseURL() + '/changePlan'

    const data = {
      plan
    }

    return this.requestSuccess(Request.post(url, JSON.stringify(data)))
  }

  static purchaseSmsCredit (amount) {
    const url = this.modelBaseURL() + '/purchaseSmsCredit'

    const data = {
      amount
    }

    return this.requestSuccess(Request.post(url, JSON.stringify(data)))
  }
}

export class BillingPlan extends APIObject {
  relations () {
    return {
      type: { type: BillingPlanType }
    }
  }

  get isPlanTypeClassic () {
    return this.type === BillingPlanType.classic
  }

  get isPlanTypeNext () {
    return this.type === BillingPlanType.next
  }
}

export class BillingPayments extends APIObject {
  relations () {
    return {
      payments: { type: BillingPayment }
    }
  }
}

export class BillingPayment extends APIObject {
  relations () {
    return {
      itemType: { type: BillingPaymentType },
      processor: { type: BillingPaymentProcessor }
    }
  }

  get processorLabel () {
    if (this.processor === BillingPaymentProcessor.braintree) {
      return 'Credit card'
    }

    if (this.processor === BillingPaymentProcessor.paypal) {
      return 'Paypal'
    }

    return 'Payment'
  }

  get net () {
    return ((100 * this.gross) / (100 + this.taxAmount)).toFixed(2)
  }

  get tax () {
    return (this.gross - this.net).toFixed(2)
  }

  get priceLabel () {
    return `${this.currency} ${Number(this.gross).toFixed(2)}`
  }

  get netLabel () {
    return `${this.currency} ${this.net}`
  }

  get taxLabel () {
    return `${this.currency} ${this.tax}`
  }

  get priceWithoutTaxLabel () {
    if (this.taxAmount) {
      return this.netLabel
    } else {
      return this.priceLabel
    }
  }
}


export class BillingMethod extends Model {
  relations () {
    return {
      type: { type: BillingMethodType }
    }
  }

  get isBillingMethodTypeCard () {
    return this.type === BillingMethodType.card
  }

  get isBillingMethodTypePaypal () {
    return this.type === BillingMethodType.paypal
  }
}

export const BillingMethodType = new Enum({
  card: { value: 'card', description: 'Card' },
  paypal: { value: 'paypal', description: 'Paypal' }
})


export class BillingStatus extends APIObject {
  relations () {
    return {
      state: { type: BillingSubscriptionState },
      plan: { type: BillingPlan },
      currentLocationPlan: { type: BillingCurrentPlan }
    }
  }

  get isBillingSubscriptionStateTrial () {
    return this.state === BillingSubscriptionState.trial
  }

  get isBillingSubscriptionStateActive () {
    return this.state === BillingSubscriptionState.active
  }

  get isBillingSubscriptionStateWarning () {
    return this.state === BillingSubscriptionState.warning
  }

  get isBillingSubscriptionStatePaymentError () {
    return this.state === BillingSubscriptionState.paymentError
  }

  get isBillingSubscriptionStateCancelled () {
    return this.state === BillingSubscriptionState.cancelled
  }

  get isBillingSubscriptionStateSuspended () {
    return this.state === BillingSubscriptionState.suspended
  }

  get isBillingSubscriptionStateInactive () {
    return this.state === BillingSubscriptionState.inactive
  }

  get isBillingSubscriptionStatePrepaid () {
    return this.state === BillingSubscriptionState.prepaid
  }

  get isBillingSubscriptionStateTemporary () {
    return this.state === BillingSubscriptionState.temporary
  }
}

export class BillingCurrentPlan extends Model {
  relations () {
    return {
      planDiscount: { type: BillingPlanDiscount }
    }
  }
}

export class BillingPlanDiscount extends Model {
  relations () {
    return {
      amountType: { type: BillingPlanDiscountType }
    }
  }

  get isAmountTypeFixed () {
    return this.amountType === BillingPlanDiscountType.fixed
  }

  get isAmountTypePercentage () {
    return this.amountType === BillingPlanDiscountType.percentage
  }
}

export const BillingSubscriptionState = new Enum({
  trial: { value: 'trial', description: 'Trial' },
  active: { value: 'active', description: 'Active' },
  warning: { value: 'warning', description: 'Warning' },
  paymentError: { value: 'payment-error', description: 'Payment Error' },
  cancelled: { value: 'cancelled', description: 'Cancelled' },
  suspended: { value: 'suspended', description: 'Suspended' },
  inactive: { value: 'inactive', description: 'Inactive' },
  prepaid: { value: 'prepaid', description: 'Pre-paid' },
  temporary: { value: 'temporary', description: 'Temporary' }
})

export const BillingPaymentType = new Enum({
  plan: { value: 0, description: 'Subscription Payment' },
  credit: { value: 1, description: 'SMS Credit' },
  auto: { value: 2, description: 'SMS Credit (auto-topup)' }
})

export const BillingPaymentProcessor = new Enum({
  braintree: { value: 'braintree', description: 'Braintree' },
  paypal: { value: 'paypal', description: 'Paypal' }
})

export const BillingPlanType = new Enum({
  classic: { value: 0, description: 'Classic' },
  next: { value: 1, description: 'Next' }
})

export const BillingPlanDiscountType = new Enum({
  percentage: { value: 1, description: 'Percentage' },
  fixed: { value: 2, description: 'Fixed' }
})

